import { OkThxWordmark } from "@/components/icons/Logo.tsx";
import { pageColumnClasses, screenGutter } from "@/components/layout/contentLayout.tsx";
import { Button } from "@/components/ui/button.tsx";
import { cn, resolveApiPath } from "@/lib/utils.ts";
import { DIAGNOSABLE_ACCESS_IDS } from "@/types/auth.ts";
import { NATIVE_CALLBACK_PARAM } from "@/types/nativeSignIn.ts";
import { providerName, providerStartPath } from "@/views/authProviders.ts";
import { Check } from "lucide-react";
import { useMemo } from "react";
import { useSearchParams } from "wouter";

/**
 * One required scope, tied to the feature it powers rather than the scope name.
 *
 * One row per id in {@link DIAGNOSABLE_ACCESS_IDS}, which is deliberately finer
 * than the vocabulary the selection screen offers — that constant says why.
 */
const REQUIRED_ACCESS: { id: string; title: string; why: string }[] = [
  {
    id: "email",
    title: "Email",
    why: "Read and organize your inbox so Okthx can triage new mail, apply labels, and draft replies for you.",
  },
  {
    id: "calendar",
    title: "Calendar",
    why: "See your schedule and create or update events so Okthx can book meetings, reschedule, and send invites.",
  },
  {
    id: "contacts",
    title: "Contacts",
    why: "Look up the people you email and meet with so Okthx can reach the right person instead of asking you for an address or a number.",
  },
  {
    id: "directory",
    title: "Work directory",
    why: "Find anyone in your organization's directory so Okthx can schedule with them by name.",
  },
];

/**
 * Shown when a login/signup OAuth grant is partial (`scope_denied`) or cancelled
 * (`access_denied`). Google's granular consent lets a user uncheck individual
 * scopes; we still reject partial grants, but explain which access is missing and
 * why, then send the user back to the provider consent screen to try again.
 *
 * Rendered inside the OAuth tab (or the native app's OAuth browser sheet), so the
 * retry link navigates this same tab back to the provider start URL.
 */
export const GrantRequired = () => {
  const [params] = useSearchParams();
  const provider = params.get("provider") ?? "google";
  const reason = params.get("reason");
  const app = params.get("app");
  const returnTo = params.get("returnTo");
  const codeChallenge = params.get("code_challenge");
  const nativeCallback = params.get(NATIVE_CALLBACK_PARAM);
  const granted = useMemo(
    () => new Set((params.get("granted") ?? "").split(",").filter(Boolean)),
    [params],
  );

  const name = providerName(provider);

  // Only what this provider can grant. Listing an item a provider has no scope
  // for would send the user back to grant something that does not exist —
  // Microsoft has no directory scope. An unknown provider falls back to Google's
  // list, matching the start-path fallback, so the page never renders an empty
  // list.
  const required = useMemo(() => {
    const ids = DIAGNOSABLE_ACCESS_IDS[provider] ?? DIAGNOSABLE_ACCESS_IDS["google"] ?? [];
    return REQUIRED_ACCESS.filter((item) => ids.includes(item.id));
  }, [provider]);

  const retryUrl = useMemo(() => {
    const base = resolveApiPath(providerStartPath(provider));
    const query = new URLSearchParams();
    if (app) query.set("app", app);
    if (returnTo) query.set("returnTo", returnTo);
    // A native sign-in has to restart with the challenge it began with: the app
    // still holds the matching verifier and will finalize against it. Dropping
    // it here would get the retry turned away as an app too old to sign in.
    if (codeChallenge) query.set("code_challenge", codeChallenge);
    // And end where the app is waiting for it.
    if (nativeCallback) query.set(NATIVE_CALLBACK_PARAM, nativeCallback);
    const qs = query.toString();
    return qs ? `${base}?${qs}` : base;
  }, [provider, app, returnTo, codeChallenge, nativeCallback]);

  const intro =
    reason === "access_denied"
      ? "Sign-in was cancelled before granting access. Okthx needs the following to work."
      : "You left out some of the access Okthx needs. Each item below powers part of the assistant, so all of it is required.";

  return (
    <div
      className={cn(
        "flex flex-col grow-1 items-center justify-center min-h-dvh",
        screenGutter,
        pageColumnClasses["8"],
      )}
    >
      <div className="flex flex-col items-center gap-8 w-full max-w-[520px] py-10">
        <div className="flex flex-col items-center gap-4 text-center">
          <OkThxWordmark className="w-[136px] h-[31.98px]" />
          <h1 className="text-2xl font-semibold">Grant access to continue</h1>
          <p className="text-muted-foreground leading-6">{intro}</p>
        </div>

        <ul className="flex flex-col gap-5 w-full">
          {required.map((item) => {
            const isGranted = granted.has(item.id);
            return (
              <li key={item.id} className="flex gap-3">
                <span
                  className="mt-0.5 flex size-5 shrink-0 items-center justify-center"
                  aria-hidden
                >
                  {isGranted ? (
                    <Check className="size-5 text-muted-foreground" />
                  ) : (
                    // A list marker, not a control. An outlined circle here read
                    // as an unchecked radio button on a page that has nothing to
                    // check: every item is required, and the only action is Try
                    // again.
                    <span className="size-1.5 rounded-full bg-muted-foreground" />
                  )}
                </span>
                <div className="flex flex-col gap-0.5">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{item.title}</span>
                    {isGranted && <span className="text-xs text-muted-foreground">Granted</span>}
                  </div>
                  <span className="text-sm text-muted-foreground leading-5">{item.why}</span>
                </div>
              </li>
            );
          })}
        </ul>

        <div className="flex flex-col items-center gap-2">
          <Button
            asChild
            variant="popover"
            className="border-default h-12 px-8"
            data-statsig-name="auth-grant-required-retry"
          >
            <a href={retryUrl}>Try again</a>
          </Button>
          <p className="text-sm text-muted-foreground text-center">
            You will be returned to the {name} consent screen.
          </p>
        </div>
      </div>
    </div>
  );
};
